import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Download, Youtube, Cloud, X, Check, Loader2,
  ChevronRight, Film, AlertCircle, ExternalLink,
  Copy, CheckCircle2, HardDrive, Upload,
} from 'lucide-react'
import { api, youtubeApi, projectApi } from '../api/client'
import type { Segment, Video, IntroOutro, MusicTrack } from '../types'
import { exportSegmentsToSingleMp4 } from '../lib/ffmpegExport'
import { CAPTION_STYLES, CaptionPreview } from '../captionStyles'
import type { CaptionStyle } from '../captionStyles'

type ExportStep = 'prepare' | 'exporting' | 'done' | 'error'

interface CloudConnection {
  provider: string
  display_name: string
  connected: boolean
  email?: string
}

const PROVIDER_META: Record<string, { icon: string; color: string }> = {
  google_drive: { icon: '📁', color: 'bg-green-600 hover:bg-green-700' },
  dropbox:      { icon: '📦', color: 'bg-blue-600 hover:bg-blue-700' },
  onedrive:     { icon: '☁️', color: 'bg-sky-600 hover:bg-sky-700' },
}

interface ExportPanelProps {
  video: Video
  segments: Segment[]
  selectedSegments: string[]
  analyzeJobId: string | null
  pipelineVideoUrl: string | null
  selectedIntro?: IntroOutro
  selectedOutro?: IntroOutro
  /** Optional music bed (from MusicPanel) — mixed under exported clips when the server supports it */
  musicTrack?: MusicTrack | null
  /** Timeline audio clips (voice/music/sfx) mixed onto the export */
  audioTracks?: { url: string; start: number; duration: number; kind: 'voice' | 'music' | 'sfx'; gain_db?: number; offset?: number }[]
  /** Timeline image B-roll clips composited onto the export video */
  imageTracks?: { url: string; start: number; duration: number }[]
  onClose: () => void
  onExportedBlob: (blob: Blob) => void
  onExportedUrl?: (url: string) => void
  projectId?: string | null
  /** Called when user clicks "Publish to YouTube" — opens the full YouTube panel */
  onOpenYouTube: () => void
  /** Caption engine/style, lifted to App so the timeline picker and Export stay in sync */
  captionStyle?: CaptionStyle
  onCaptionStyleChange?: (style: CaptionStyle) => void
  karaokeMode?: boolean
  onKaraokeToggle?: () => void
}

export function ExportPanel({
  video,
  segments,
  selectedSegments,
  analyzeJobId,
  pipelineVideoUrl,
  selectedIntro,
  selectedOutro,
  musicTrack,
  audioTracks,
  imageTracks,
  onClose,
  onExportedBlob,
  onExportedUrl,
  projectId,
  onOpenYouTube,
  captionStyle: propCaptionStyle,
  onCaptionStyleChange,
  karaokeMode: propKaraokeMode,
  onKaraokeToggle,
}: ExportPanelProps) {
  const [step, setStep] = useState<ExportStep>('prepare')
  const [progress, setProgress] = useState(0)
  const [statusMessage, setStatusMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null)
  const [allClips, setAllClips] = useState<{ filename: string; download_url: string }[]>([])
  const [downloadFilename, setDownloadFilename] = useState('export.mp4')
  const [copied, setCopied] = useState(false)
  const [exportedBlob, setExportedBlob] = useState<Blob | null>(null)
  const [mixing, setMixing] = useState(false)
  const [mixed, setMixed] = useState(false)
  const abortRef = useRef(false)

  // Cloud storage state
  // Export options
  const [aspectRatio, setAspectRatio] = useState<'landscape' | 'vertical' | 'square'>('vertical')
  const [captionsEnabled, setCaptionsEnabled] = useState(true)
  // Caption engine/style: use the lifted App state when provided (keeps the timeline
  // header picker and this panel in sync), otherwise fall back to local state.
  const [localCaptionStyle, setLocalCaptionStyle] = useState<CaptionStyle>('dynamic_viral')
  const captionStyle = propCaptionStyle ?? localCaptionStyle
  const setCaptionStyle = onCaptionStyleChange ?? setLocalCaptionStyle
  const [localKaraoke, setLocalKaraoke] = useState(false)
  const karaokeMode = propKaraokeMode ?? localKaraoke
  const toggleKaraoke = onKaraokeToggle ?? (() => setLocalKaraoke(v => !v))
  const [quality, setQuality] = useState<'720p' | '1080p'>('1080p')

  // Cloud storage state
  const [cloudConnections, setCloudConnections] = useState<CloudConnection[]>([])
  const [cloudUploading, setCloudUploading] = useState<Record<string, boolean>>({})
  const [cloudDone, setCloudDone] = useState<Record<string, boolean>>({})
  const [cloudError, setCloudError] = useState<Record<string, string>>({})

  // Fetch cloud providers + connections on mount
  useEffect(() => {
    (async () => {
      try {
        const [provRes, connRes] = await Promise.allSettled([
          fetch('/api/storage/providers'),
          fetch('/api/storage/connections'),
        ])

        const providers: Set<string> = new Set()
        if (provRes.status === 'fulfilled' && provRes.value.ok) {
          const data = await provRes.value.json()
          ;(data.providers || []).forEach((p: any) => providers.add(p.name || p.key))
        }

        const connected: Record<string, { email?: string }> = {}
        if (connRes.status === 'fulfilled' && connRes.value.ok) {
          const data = await connRes.value.json()
          ;(data.connections || []).forEach((c: any) => {
            connected[c.provider] = { email: c.email || c.user_email }
          })
        }

        // Build connection list from the known providers
        const display: Record<string, string> = {
          google_drive: 'Google Drive',
          dropbox: 'Dropbox',
          onedrive: 'OneDrive',
        }
        const conns: CloudConnection[] = []
        for (const key of Object.keys(display)) {
          if (providers.has(key)) {
            conns.push({
              provider: key,
              display_name: display[key],
              connected: !!connected[key],
              email: connected[key]?.email,
            })
          }
        }
        setCloudConnections(conns)
      } catch {
        // non-fatal — cloud buttons just won't show
      }
    })()
  }, [])

  const clipsToExport = segments.filter(s => selectedSegments.includes(s.id))

  const totalDuration = clipsToExport.reduce((sum, s) => {
    const start = Math.max(0, s.start - (s.padding?.before || 0))
    const end = Math.min(video.duration || s.end, s.end + (s.padding?.after || 0))
    return sum + (end - start)
  }, 0)

  const formatDuration = (secs: number) => {
    const m = Math.floor(secs / 60)
    const s = Math.floor(secs % 60)
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  const safeTitle = (video.title || 'clip')
    .replace(/[^a-z0-9\- _]/gi, '')
    .trim()
    .slice(0, 80) || 'clip'

  // ── Run Export ──────────────────────────────────────────────
  const runExport = useCallback(async () => {
    setStep('exporting')
    setProgress(0)
    setStatusMessage('Preparing clips…')
    abortRef.current = false

    try {
      const segmentsToExport = clipsToExport.map(s => ({
        ...s,
        effective_start: Math.max(0, s.start - (s.padding?.before || 0)),
        effective_end: Math.min(video.duration || s.end, s.end + (s.padding?.after || 0)),
      }))

      // ── Local / sample video: use in-browser ffmpeg.wasm ──
      if (video.url?.startsWith('/samples/')) {
        setStatusMessage('Loading video into browser…')
        setProgress(10)

        const blob = await exportSegmentsToSingleMp4({
          sourceUrl: video.url,
          segments: segmentsToExport
            .map(s => ({ start: Number(s.effective_start), end: Number(s.effective_end) }))
            .sort((a, b) => a.start - b.start),
          outputFileName: `${safeTitle}_export.mp4`,
        })

        if (abortRef.current) return

        setExportedBlob(blob)
        onExportedBlob(blob)

        const url = URL.createObjectURL(blob)
        setDownloadUrl(url)
        setDownloadFilename(`${safeTitle}_export.mp4`)
        setProgress(100)
        setStep('done')
        setStatusMessage('Export complete!')
        return
      }

      // ── Pipeline video: server-side export ──
      setStatusMessage('Sending to server…')
      setProgress(15)

      const response = await api.post('/studio/export', {
        project_id: video.id,
        video_id: video.id,
        segments: segmentsToExport,
        analyze_job_id: analyzeJobId,
        video_url: pipelineVideoUrl || video.url,
        intro_id: selectedIntro?.id,
        outro_id: selectedOutro?.id,
        music: musicTrack
          ? { stream_path: musicTrack.stream_path, gain_db: -16 }
          : undefined,
        format: 'mp4',
        quality,
        aspect_ratio: aspectRatio,
        add_captions: captionsEnabled,
        caption_style: captionStyle,
        karaoke_mode: karaokeMode,
      })

      if (!response.data?.success) {
        throw new Error(response.data?.error || 'Export request failed')
      }

      // Direct download URLs returned immediately
      if (Array.isArray(response.data.download_urls) && response.data.download_urls.length > 0) {
        setDownloadUrl(response.data.download_urls[0])
        setDownloadFilename(`${safeTitle}_export.mp4`)
        setProgress(100)
        setStep('done')
        setStatusMessage('Export complete!')
        return
      }

      // Queued — poll for completion
      const exportId: string | undefined = response.data.export_job_id
      const statusUrl = response.data.status_url || (exportId ? `/studio/export/${exportId}` : '')
      const fallbackUrl = response.data.download_url || (exportId ? `/api/job/${exportId}/download` : '')

      if (!statusUrl) {
        if (fallbackUrl) {
          setDownloadUrl(fallbackUrl)
          setProgress(100)
          setStep('done')
          return
        }
        throw new Error('No status URL returned from server')
      }

      setStatusMessage('Server is processing your clips…')
      setProgress(30)

      // Poll loop
      const startedAt = Date.now()
      const maxWaitMs = 10 * 60 * 1000

      const poll = (): Promise<void> => new Promise((resolve, reject) => {
        const tick = async () => {
          if (abortRef.current) { resolve(); return }

          if (Date.now() - startedAt > maxWaitMs) {
            // Timeout — still give them a download link
            setDownloadUrl(fallbackUrl || null)
            setStep('done')
            setStatusMessage('Export is still processing. You can download when ready.')
            resolve()
            return
          }

          try {
            const statusResp = await api.get(statusUrl)
            const status = (statusResp.data?.status || '').toLowerCase()
            const dl = statusResp.data?.download_url

            // Real progress + ETA from the pipeline stages (server-computed);
            // fall back to an elapsed-based estimate if not available yet.
            const srvProg = statusResp.data?.progress
            const eta = statusResp.data?.eta_seconds
            const stage = statusResp.data?.stage
            if (typeof srvProg === 'number' && srvProg > 0) {
              setProgress(Math.round(srvProg))
            } else {
              const elapsed = (Date.now() - startedAt) / 1000
              setProgress(Math.round(Math.min(95, 30 + elapsed * 0.5)))
            }
            if (stage) {
              const etaTxt = (typeof eta === 'number' && eta > 0)
                ? ` · ~${eta < 60 ? eta + 's' : Math.ceil(eta / 60) + 'm'} left` : ''
              setStatusMessage(`${stage}${etaTxt}`)
            }

            if (status === 'completed' || status === 'complete' || status === 'done') {
              if (Array.isArray(statusResp.data?.clips) && statusResp.data.clips.length > 0) {
                setAllClips(statusResp.data.clips)
              }
              setDownloadUrl(dl || fallbackUrl)
              setDownloadFilename(`${safeTitle}_export.mp4`)
              setProgress(100)
              setStep('done')
              setStatusMessage('Export complete!')
              resolve()
              return
            }
            if (status === 'failed' || status === 'error') {
              reject(new Error(statusResp.data?.error || 'Export failed on server'))
              return
            }

            // Friendly status
            if (status.includes('transcrib')) setStatusMessage('Transcribing audio…')
            else if (status.includes('caption')) setStatusMessage('Burning captions…')
            else if (status.includes('render')) setStatusMessage('Rendering video…')
            else if (status.includes('upload')) setStatusMessage('Uploading result…')
            else setStatusMessage('Processing clips…')

          } catch {
            // keep polling
          }

          setTimeout(tick, 2000)
        }
        setTimeout(tick, 2000)
      })

      await poll()

    } catch (err: any) {
      if (abortRef.current) return
      const msg = err?.response?.data?.error || err?.message || 'Export failed'
      setErrorMessage(msg)
      setStep('error')
    }
  }, [clipsToExport, video, analyzeJobId, pipelineVideoUrl, selectedIntro, selectedOutro, musicTrack, safeTitle, onExportedBlob, aspectRatio, captionsEnabled, captionStyle, karaokeMode, quality])

  // ── Fetch blob from download URL for YouTube/cloud uploads ─
  // For server-side exports we only get a URL, not a blob.
  // Fetch it in the background so YouTube publish has a blob ready.
  useEffect(() => {
    if (step !== 'done' || exportedBlob || !downloadUrl) return
    if (downloadUrl.startsWith('blob:')) return // already a blob URL
    if (audioTracks && audioTracks.length && !mixed) return // let the mix pass produce the blob

    let cancelled = false
    ;(async () => {
      try {
        const resp = await fetch(downloadUrl)
        if (!resp.ok || cancelled) return
        const blob = await resp.blob()
        if (cancelled) return
        setExportedBlob(blob)
        onExportedBlob(blob)
      } catch {
        // non-fatal — YouTube will show "no clip" but download still works
      }
    })()
    return () => { cancelled = true }
  }, [step, downloadUrl, exportedBlob, onExportedBlob, audioTracks, mixed])

  // ── Mix the timeline's Voice/Music/SFX onto the rendered export ─
  useEffect(() => {
    if (step !== 'done' || !downloadUrl || downloadUrl.startsWith('blob:')) return
    if (((!audioTracks || audioTracks.length === 0) && (!imageTracks || imageTracks.length === 0)) || mixed || mixing) return
    let cancelled = false
    ;(async () => {
      setMixing(true)
      setStatusMessage((imageTracks && imageTracks.length) ? 'Compositing your video…' : 'Mixing audio tracks…')
      try {
        // Cut-aware: map each track's SOURCE-timeline position into the concatenated export OUTPUT.
        const effSegs = clipsToExport
          .map(sg => ({ a: Math.max(0, sg.start - (sg.padding?.before || 0)), b: Math.min(video.duration || sg.end, sg.end + (sg.padding?.after || 0)) }))
          .sort((x, y) => x.a - y.a)
        const introOffset = (selectedIntro && typeof (selectedIntro as any).duration === 'number') ? (selectedIntro as any).duration : 0
        const mapToOutput = (tt: number): number | null => {
          let off = introOffset
          for (const sg of effSegs) {
            if (tt >= sg.a && tt <= sg.b) return off + (tt - sg.a)
            if (tt < sg.a) return off // in a cut-out gap before this segment -> snap to its start
            off += Math.max(0, sg.b - sg.a)
          }
          return null // beyond the last exported segment
        }
        const tracks = audioTracks
          .map(t => {
            let st = mapToOutput(t.start)
            if (st === null) { if (t.kind === 'music') st = introOffset; else return null } // music -> bed; voice/sfx in cut content -> drop
            const g = t.gain_db ?? (t.kind === 'music' ? -14 : t.kind === 'sfx' ? -6 : 0)
            return { url: t.url, start: st as number, gain_db: g, offset: t.offset ?? 0, duration: t.duration }
          })
          .filter((x): x is { url: string; start: number; gain_db: number; offset: number; duration: number } => x !== null)
        const images = (imageTracks || [])
          .map(im => { const st = mapToOutput(im.start); if (st === null) return null; return { url: im.url, start: st, duration: im.duration } })
          .filter((x): x is { url: string; start: number; duration: number } => x !== null)
        if (tracks.length === 0 && images.length === 0) { setMixed(true); setStatusMessage('Export complete.'); return }
        const resp = await api.post('/studio/export/mix', { clip_url: downloadUrl, tracks, images }, { responseType: 'blob', timeout: 300000 })
        if (cancelled) return
        const blob = resp.data as Blob
        setExportedBlob(blob)
        onExportedBlob(blob)
        setDownloadUrl(URL.createObjectURL(blob))
        setMixed(true)
        setStatusMessage('Export complete — audio mixed in!')
      } catch (err) {
        if (!cancelled) { setMixed(true); setStatusMessage('Export complete (couldn’t mix audio — using base clip).') }
      } finally {
        if (!cancelled) setMixing(false)
      }
    })()
    return () => { cancelled = true }
  }, [step, downloadUrl, audioTracks, imageTracks, mixed, mixing, onExportedBlob])

  // ── Notify parent of download URL so YouTube panel can use it ─
  useEffect(() => {
    if (downloadUrl && onExportedUrl) onExportedUrl(downloadUrl)
  }, [downloadUrl, onExportedUrl])

  // ── Record export in project history ─
  useEffect(() => {
    if (step !== 'done' || !downloadUrl) return
    projectApi.recordExport({
      project_id: projectId || undefined,
      filename: downloadFilename,
      file_url: downloadUrl,
      export_type: 'download',
      clip_count: clipsToExport.length,
    }).catch(() => {}) // fire-and-forget
  }, [step, downloadUrl])

  // ── Download handler ───────────────────────────────────────
  const handleDownload = () => {
    if (!downloadUrl) return
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = downloadFilename
    link.click()
  }

  // ── Copy link ──────────────────────────────────────────────
  const handleCopyLink = async () => {
    if (!downloadUrl) return
    try {
      const fullUrl = downloadUrl.startsWith('http')
        ? downloadUrl
        : `${window.location.origin}${downloadUrl}`
      await navigator.clipboard.writeText(fullUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch { /* clipboard not available */ }
  }

  // ── Cleanup blob URL on unmount ────────────────────────────
  useEffect(() => {
    return () => {
      abortRef.current = true
      if (downloadUrl?.startsWith('blob:')) URL.revokeObjectURL(downloadUrl)
    }
  }, [downloadUrl])

  // ── Cloud save — upload exported clip to provider ───────────
  const handleCloudUpload = async (provider: string) => {
    // Need either a blob or a download URL we can fetch
    let blob = exportedBlob
    if (!blob && downloadUrl) {
      try {
        const resp = await fetch(downloadUrl)
        blob = await resp.blob()
      } catch {
        setCloudError(prev => ({ ...prev, [provider]: 'Could not fetch exported file' }))
        return
      }
    }
    if (!blob) {
      setCloudError(prev => ({ ...prev, [provider]: 'No exported file available' }))
      return
    }

    setCloudUploading(prev => ({ ...prev, [provider]: true }))
    setCloudError(prev => ({ ...prev, [provider]: '' }))

    try {
      const formData = new FormData()
      formData.append('file', blob, downloadFilename)
      formData.append('filename', downloadFilename)
      formData.append('folder', '/legit2clip')

      const resp = await fetch(`/api/storage/${provider}/upload`, {
        method: 'POST',
        body: formData,
      })
      const data = await resp.json()

      if (data.success) {
        setCloudDone(prev => ({ ...prev, [provider]: true }))
      } else {
        setCloudError(prev => ({ ...prev, [provider]: data.error || 'Upload failed' }))
      }
    } catch (err: any) {
      setCloudError(prev => ({ ...prev, [provider]: err?.message || 'Upload failed' }))
    } finally {
      setCloudUploading(prev => ({ ...prev, [provider]: false }))
    }
  }

  // ── Render ─────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center" onClick={onClose}>
      <div
        className="bg-gray-800 rounded-2xl w-full max-w-xl shadow-2xl border border-gray-700 overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
              step === 'done' ? 'bg-green-500/20' :
              step === 'error' ? 'bg-red-500/20' :
              'bg-indigo-500/20'
            }`}>
              {step === 'done' ? <Check size={18} className="text-green-400" /> :
               step === 'error' ? <AlertCircle size={18} className="text-red-400" /> :
               step === 'exporting' ? <Loader2 size={18} className="text-indigo-400 animate-spin" /> :
               <Film size={18} className="text-indigo-400" />}
            </div>
            <div>
              <h2 className="text-white font-semibold text-lg leading-tight">
                {step === 'prepare' ? 'Export Clips' :
                 step === 'exporting' ? 'Exporting…' :
                 step === 'done' ? 'Export Complete' :
                 'Export Failed'}
              </h2>
              <p className="text-gray-400 text-xs">
                {clipsToExport.length} clip{clipsToExport.length !== 1 ? 's' : ''} • {formatDuration(totalDuration)}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors p-1">
            <X size={20} />
          </button>
        </div>

        {/* ── Body ── */}
        <div className="px-6 py-5">

          {/* STEP: Prepare */}
          {step === 'prepare' && (
            <div className="space-y-4">
              {/* Clip list */}
              <div className="space-y-1.5 max-h-32 overflow-y-auto pr-1">
                {clipsToExport.map((clip, i) => {
                  const start = Math.max(0, clip.start - (clip.padding?.before || 0))
                  const end = Math.min(video.duration || clip.end, clip.end + (clip.padding?.after || 0))
                  return (
                    <div key={clip.id} className="flex items-center gap-3 bg-gray-700/50 rounded-lg px-3 py-2">
                      <span className="text-xs text-gray-500 font-mono w-5">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white truncate">{clip.text || `Clip ${i + 1}`}</p>
                        <p className="text-xs text-gray-400">
                          {formatDuration(start)} → {formatDuration(end)} ({formatDuration(end - start)})
                        </p>
                      </div>
                    </div>
                  )
                })}
              </div>

              {/* Intro/Outro info */}
              {(selectedIntro || selectedOutro) && (
                <div className="flex gap-2">
                  {selectedIntro && (
                    <span className="px-2 py-1 bg-green-900/40 text-green-300 rounded text-xs">
                      🎬 Intro: {selectedIntro.name}
                    </span>
                  )}
                  {selectedOutro && (
                    <span className="px-2 py-1 bg-blue-900/40 text-blue-300 rounded text-xs">
                      🎬 Outro: {selectedOutro.name}
                    </span>
                  )}
                </div>
              )}

              {/* ── Export Settings ── */}
              <div className="border-t border-gray-700 pt-3 space-y-3">

                {/* Aspect Ratio */}
                <div>
                  <label className="text-xs text-gray-400 block mb-1.5">Aspect Ratio</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { value: 'vertical' as const, label: '9:16', desc: 'Shorts / TikTok' },
                      { value: 'landscape' as const, label: '16:9', desc: 'YouTube' },
                      { value: 'square' as const, label: '1:1', desc: 'Instagram' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setAspectRatio(opt.value)}
                        className={`px-3 py-2 rounded-lg text-center transition-colors border ${
                          aspectRatio === opt.value
                            ? 'border-indigo-500 bg-indigo-500/20 text-white'
                            : 'border-gray-600 bg-gray-700/50 text-gray-300 hover:border-gray-500'
                        }`}
                      >
                        <span className="block text-sm font-medium">{opt.label}</span>
                        <span className="block text-[10px] text-gray-400 mt-0.5">{opt.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Captions */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs text-gray-400">Burn-in Captions</label>
                    <button
                      onClick={() => setCaptionsEnabled(!captionsEnabled)}
                      className={`relative w-9 h-5 rounded-full transition-colors ${
                        captionsEnabled ? 'bg-indigo-500' : 'bg-gray-600'
                      }`}
                    >
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                        captionsEnabled ? 'left-[18px]' : 'left-0.5'
                      }`} />
                    </button>
                  </div>

                  {captionsEnabled && (
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-1.5">
                        {CAPTION_STYLES.map(opt => (
                          <button
                            key={opt.value}
                            onClick={() => setCaptionStyle(opt.value)}
                            className={`rounded-lg text-left transition-colors border overflow-hidden ${
                              captionStyle === opt.value
                                ? 'border-indigo-500 ring-1 ring-indigo-400/50'
                                : 'border-gray-600 hover:border-gray-500'
                            }`}
                          >
                            <span className="flex items-end justify-center h-11 bg-black px-1.5 pb-1 overflow-hidden">
                              <CaptionPreview spec={opt} size={12} />
                            </span>
                            <span className={`block px-2 py-1 ${captionStyle === opt.value ? 'bg-indigo-500/20 text-white' : 'bg-gray-700/50 text-gray-300'}`}>
                              <span className="block text-xs font-medium">{opt.label}</span>
                              <span className="block text-[10px] text-gray-500 mt-0.5">{opt.desc}</span>
                            </span>
                          </button>
                        ))}
                      </div>

                      {/* Karaoke toggle */}
                      <div className="flex items-center justify-between px-1">
                        <div>
                          <span className="text-xs text-gray-300">Word-by-word highlight</span>
                          <span className="block text-[10px] text-gray-500">Karaoke-style word tracking</span>
                        </div>
                        <button
                          onClick={() => toggleKaraoke()}
                          className={`relative w-9 h-5 rounded-full transition-colors ${
                            karaokeMode ? 'bg-indigo-500' : 'bg-gray-600'
                          }`}
                        >
                          <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                            karaokeMode ? 'left-[18px]' : 'left-0.5'
                          }`} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Quality */}
                <div>
                  <label className="text-xs text-gray-400 block mb-1.5">Quality</label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { value: '1080p' as const, label: '1080p', desc: 'Full HD' },
                      { value: '720p' as const, label: '720p', desc: 'Faster export' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setQuality(opt.value)}
                        className={`px-3 py-2 rounded-lg text-center transition-colors border ${
                          quality === opt.value
                            ? 'border-indigo-500 bg-indigo-500/20 text-white'
                            : 'border-gray-600 bg-gray-700/50 text-gray-300 hover:border-gray-500'
                        }`}
                      >
                        <span className="text-sm font-medium">{opt.label}</span>
                        <span className="block text-[10px] text-gray-400">{opt.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Export button */}
              <button
                onClick={runExport}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors font-medium"
              >
                <Download size={18} />
                Export {clipsToExport.length} Clip{clipsToExport.length !== 1 ? 's' : ''}
                <ChevronRight size={16} className="opacity-60" />
              </button>
            </div>
          )}

          {/* STEP: Exporting */}
          {step === 'exporting' && (
            <div className="space-y-4 py-4">
              <div className="text-center">
                <Loader2 size={40} className="text-indigo-400 animate-spin mx-auto mb-3" />
                <p className="text-white font-medium">{statusMessage}</p>
              </div>

              {/* Progress bar */}
              <div className="w-full bg-gray-700 rounded-full h-2 overflow-hidden">
                <div
                  className="h-full bg-indigo-500 rounded-full transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-center text-xs text-gray-400">{progress}%</p>
            </div>
          )}

          {/* STEP: Done */}
          {step === 'done' && (
            <div className="space-y-5">
              {/* Success badge */}
              <div className="text-center py-2">
                <div className="w-14 h-14 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCircle2 size={28} className="text-green-400" />
                </div>
                <p className="text-white font-medium">{statusMessage || 'Your clips are ready!'}</p>
              </div>

              {/* Action buttons */}
              <div className="space-y-2">
                {/* Download */}
                <button
                  onClick={handleDownload}
                  disabled={!downloadUrl}
                  className="w-full flex items-center gap-3 px-4 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors font-medium disabled:opacity-50"
                >
                  <Download size={18} />
                  <div className="flex-1 text-left">
                    <span className="block text-sm">Download to device</span>
                    <span className="block text-xs text-indigo-200 opacity-70">{downloadFilename}</span>
                  </div>
                  <ChevronRight size={16} className="opacity-60" />
                </button>

                {/* Every delivered clip (multi-clip exports) */}
                {allClips.length > 1 && (
                  <div className="space-y-1.5">
                    {allClips.map((c, i) => (
                      <a
                        key={c.download_url}
                        href={c.download_url}
                        download={c.filename || `clip_${i + 1}.mp4`}
                        className="w-full flex items-center gap-3 px-4 py-2.5 bg-gray-800 text-white rounded-xl hover:bg-gray-700 transition-colors text-sm"
                      >
                        <Download size={15} className="text-indigo-300" />
                        <span className="flex-1 text-left truncate">{c.filename || `Clip ${i + 1}`}</span>
                        <ChevronRight size={14} className="opacity-50" />
                      </a>
                    ))}
                  </div>
                )}

                {/* YouTube */}
                <button
                  onClick={() => {
                    onClose()
                    // Small delay so modal unmounts before YouTube panel mounts
                    setTimeout(() => onOpenYouTube(), 100)
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 bg-gray-700 text-white rounded-xl hover:bg-gray-600 transition-colors"
                >
                  <Youtube size={18} className="text-red-400" />
                  <div className="flex-1 text-left">
                    <span className="block text-sm">Publish to YouTube</span>
                    <span className="block text-xs text-gray-400">Upload as Short or video</span>
                  </div>
                  <ChevronRight size={16} className="opacity-60" />
                </button>

                {/* Copy link (for server exports) */}
                {downloadUrl && !downloadUrl.startsWith('blob:') && (
                  <button
                    onClick={handleCopyLink}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-gray-700 text-white rounded-xl hover:bg-gray-600 transition-colors"
                  >
                    {copied ? <CheckCircle2 size={18} className="text-green-400" /> : <Copy size={18} className="text-gray-400" />}
                    <div className="flex-1 text-left">
                      <span className="block text-sm">{copied ? 'Link copied!' : 'Copy download link'}</span>
                      <span className="block text-xs text-gray-400">Share with anyone</span>
                    </div>
                  </button>
                )}

                {/* Cloud Storage Providers */}
                {cloudConnections.length > 0 && (
                  <>
                    <div className="border-t border-gray-700 pt-3 mt-1">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Save to Cloud</p>
                    </div>
                    {cloudConnections.map(conn => {
                      const meta = PROVIDER_META[conn.provider] || { icon: '☁️', color: 'bg-gray-600 hover:bg-gray-500' }
                      const uploading = cloudUploading[conn.provider]
                      const done = cloudDone[conn.provider]
                      const error = cloudError[conn.provider]

                      if (!conn.connected) {
                        return (
                          <button
                            key={conn.provider}
                            onClick={() => window.open(`/api/storage/connect/${conn.provider}`, '_blank')}
                            className="w-full flex items-center gap-3 px-4 py-3 bg-gray-700/50 text-gray-400 rounded-xl hover:bg-gray-700 transition-colors"
                          >
                            <span className="text-lg">{meta.icon}</span>
                            <div className="flex-1 text-left">
                              <span className="block text-sm">{conn.display_name}</span>
                              <span className="block text-xs text-gray-500">Click to connect</span>
                            </div>
                            <ExternalLink size={14} className="opacity-40" />
                          </button>
                        )
                      }

                      return (
                        <button
                          key={conn.provider}
                          onClick={() => !done && !uploading && handleCloudUpload(conn.provider)}
                          disabled={uploading || done}
                          className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                            done ? 'bg-green-900/30 text-green-300' :
                            uploading ? 'bg-gray-700/50 text-gray-300 cursor-wait' :
                            'bg-gray-700 text-white hover:bg-gray-600'
                          }`}
                        >
                          {uploading ? (
                            <Loader2 size={18} className="animate-spin text-gray-400" />
                          ) : done ? (
                            <CheckCircle2 size={18} className="text-green-400" />
                          ) : (
                            <span className="text-lg">{meta.icon}</span>
                          )}
                          <div className="flex-1 text-left">
                            <span className="block text-sm">
                              {done ? `Saved to ${conn.display_name}` :
                               uploading ? `Uploading to ${conn.display_name}…` :
                               `Save to ${conn.display_name}`}
                            </span>
                            <span className="block text-xs text-gray-400">
                              {error || conn.email || '/legit2clip folder'}
                            </span>
                          </div>
                          {!done && !uploading && <Upload size={14} className="opacity-40" />}
                        </button>
                      )
                    })}
                  </>
                )}
              </div>
            </div>
          )}

          {/* STEP: Error */}
          {step === 'error' && (
            <div className="space-y-4 py-4">
              <div className="text-center">
                <div className="w-14 h-14 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <AlertCircle size={28} className="text-red-400" />
                </div>
                <p className="text-white font-medium mb-1">Export failed</p>
                <p className="text-sm text-gray-400">{errorMessage}</p>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={runExport}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors font-medium"
                >
                  Try Again
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-700 text-white rounded-xl hover:bg-gray-600 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
